//Imports
import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { HasPermission } from "Core/security";

//Hooks
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

//Actions
import { reset, manageSearchInputs } from "./redux/slice";
import { sendGenericExport, clearRedux, savePreference } from "Redux/actions";
import { openModalCreateEditManageInput } from "./modals/createEditManageInput/redux/slice";

//Controls
import {
  Container,
  IconButton,
  MenuComponent,
  SearchInput,
  TableButtons,
  TableView,
  useTablePagination
} from "@gpstrackit/gtc-react-controls";

//Components
import { Grid } from "@mui/material";
import { NotificationManager } from "../../../../components/Notifications";
import Preferences from "Core/containers/Preferences";

//Modals
import CreateEditManageInput from "./modals/createEditManageInput";
import { openModalDeleteManageInput } from "./modals/deleteManageInput/redux/slice";
import DeleteManageInput from "./modals/deleteManageInput";

//Helpers
import { completeColumns } from "Core/data/Helpers";
import { disableItemSelection } from "../../.globals/utils";

const KEY_PREFERENCE = "Manage-InputsTab-list";
const INITIAL_SORT = { key: "title", order: "asc" };

const InputsTab = () => {
  //States
  const [selectedItems, setSelectedItems] = useState([]);
  const [preferences, setPreferences] = useState({});
  const [loadPreferences, setLoadPreferences] = useState(true);
  const [loadColumns, setLoadColumns] = useState(false);

  //Table refs
  const tableRef = useRef();

  //Language
  const { messages, locale } = useIntl();

  //Redux
  const authenticatedId = useSelector((state) => state.securityRedux.authenticatedId);
  const { loading, inputs, total, error } = useSelector((state) => state.manageInputsTabRedux);
  const openCreateEditManageInput = useSelector(
    (state) => state.manageCreateEditManageInputRedux.open
  );
  const openDeleteManageInput = useSelector((state) => state.manageDeleteManageInputRedux.open);
  const exportResult = useSelector((state) => state.commomRedux.exportResult);
  const exportName = useSelector((state) => state.commomRedux.exportName);
  const dispatch = useDispatch();
  const pagination = useTablePagination({
    dispatch,
    action: manageSearchInputs,
    exportName: "inputs",
    searchFields: ["title"],
    sendGenericExport,
    tableRef
  });

  // Permissions
  const user = useSelector((state) => state.securityRedux.user);
  const editablePermissions = HasPermission(user, "", [340]);

  //Memos
  const currentColumns = useMemo(() => [
    {
      key: "title",
      title: messages["manage_inputs_columTitle"],
      show: true,
      sort: true,
      export: true,
      render: (row) => {
        return (
          <Grid
            container
            alignItems="center"
            justifyContent="flex-start"
            className="context-menu-container"
            wrap="nowrap"
          >
            <Grid item zeroMinWidth>
              <p>{row?.title}</p>
            </Grid>
            <Grid item>
              {editablePermissions &&
                <MenuComponent
                  isDisable={loading}
                  options={[messages["edit"], messages["delete"]]}
                  icon="more_vert"
                  className="showInHover"
                  placement="right-start"
                  onClick={(type, value) => {
                  if (type === messages["edit"]) {
                    dispatch(openModalCreateEditManageInput(value));
                  } else if (type === messages["delete"]) {
                    dispatch(openModalDeleteManageInput({ deleteInputList: [value] }));
                  }
                }}
                value={row}
              />
              }
            </Grid>
          </Grid>
        );
      },
      exportValue: (row) => {
        return row?.name;
      }
    },
    {
      key: "onLabel",
      title: messages["manage_inputs_onLabel"],
      show: true,
      export: true
    },
    {
      key: "offLabel",
      title: messages["manage_inputs_offLabel"],
      show: true,
      export: true
    }
  ], [locale]);

  //Helpers
  const handleDeleteInputs = useCallback(() => {
    dispatch(openModalDeleteManageInput({ deleteInputList: selectedItems }));
  }, [selectedItems]);

  const savePreferences = useCallback(
    (preferencesArray) => {
      if (!Array.isArray(preferencesArray)) {
        console.error("Expected an array of preferences");
        return;
      }

      let updatedPreferences = { ...preferences } || {};

      preferencesArray.forEach(({ key, value }) => {
        updatedPreferences[key] = value;
      });

      setPreferences(updatedPreferences);

      dispatch(
        savePreference({
          entityName: "User",
          entityId: authenticatedId,
          preferenceKey: KEY_PREFERENCE,
          preferenceValue: updatedPreferences
        })
      );
    },
    [preferences]
  );

  //Effects
  useEffect(() => {
    if (preferences?.columns?.length && !loadColumns) {
      const preferenceMap = new Map(preferences.columns.map((col) => [col.key, col]));

      // Reorder columns based on preferences (keeping `show`)
      const reorderedColumns = preferences.columns
        .map((pref) => {
          const col = currentColumns.find((c) => c.key === pref.key);
          return col ? { ...col, show: pref.show } : null;
        })
        .filter(Boolean);

      // Add new columns (not listed in preferences), with `show: true` by default
      const newColumns = currentColumns
        .filter((col) => !preferenceMap.has(col.key))
        .map((col) => ({ ...col, show: true }));

      const finalColumns = [...reorderedColumns, ...newColumns];

      pagination.setColumns(finalColumns);
      setPreferences({ ...preferences });
      setLoadColumns(true);
    }
  }, [preferences.columns, loadColumns]);

  useEffect(() => {
    if (pagination.columns.length > 0) {
      setPreferences({ ...preferences, columns: pagination.columns });
      setLoadColumns(false);
    }
  }, [locale]);

  useEffect(() => {
    pagination.setColumns(currentColumns);
  }, [currentColumns]);

  useEffect(() => {
    if (!error) return;
    NotificationManager.error(
      messages["errorMessage"],
      messages["error"],
      3000,
      null,
      null,
      "warning"
    );
    dispatch(reset({ error: null }));
  }, [error]);

  useEffect(() => {
    if (!exportResult || exportName !== "inputs") return;

    NotificationManager.info(
      messages["exportInProcessBody"],
      messages["exportInProcessTitle"],
      4000,
      null,
      null,
      "info",
      null
    );
    dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
  }, [exportResult, exportName]);
  return (
    <>
      <Container maxWidth={false}>
        <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <Grid item xs={6} sm={2}>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={1}
            >
              <Grid item style={{ display: "flex" }}>
                {editablePermissions &&
                  <IconButton
                    tooltip={messages["add"]}
                    placement="bottom"
                    variant="secondary"
                    icon="add_box"
                    size="small"
                    style={{ opacity: loading ? "0.6" : "1" }}
                    isDisabled={loading}
                    onClick={() => dispatch(openModalCreateEditManageInput())}
                  />
                }
                {editablePermissions &&
                  <IconButton
                    tooltip={messages["delete"]}
                    placement="bottom"
                    variant="secondary"
                    icon="delete"
                    size="small"
                    onClick={() => handleDeleteInputs()}
                    style={{ opacity: !selectedItems.length && "0.6" }}
                    isDisabled={!selectedItems.length}
                  />
                }
                <TableButtons
                  messages={messages}
                  buttons={["export", "orderColumn"]}
                  {...pagination}
                  onChangeColumns={(columns) => {
                    savePreferences([{ key: "columns", value: columns }]);
                    pagination.setColumns(columns);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={4} className="mb-10 text-right">
            <SearchInput
              placeholder={messages["manage_inputs_search_by_input_label"]}
              searchText={pagination.searchText}
              onSearchText={pagination.onSearchChange}
            />
          </Grid>
        </Grid>
        <Preferences
          preferencesKey={KEY_PREFERENCE}
          onInitialPreferences={(data) => {
            if (!loadPreferences) return;
            setTimeout(() => {
              let sort = data?.sort || INITIAL_SORT; // * Initial sort
              tableRef.current?.setSort?.(sort);
              pagination.onChangeSort(sort);
            }, 100);
            setPreferences(data);
            setLoadPreferences(false);
          }}
        >
          <TableView
            keyColumn="id"
            loading={loading || loadPreferences}
            data={inputs}
            total={total}
            topheight={235}
            disableItemSelection={disableItemSelection(pagination)}
            exportOrientation="landscape"
            onSelectedItems={(items) => setSelectedItems(items)}
            {...pagination}
            onChangeColumns={(columns) => {
              const newcurrentColumns = pagination?.columns || [];
              const finalsColumns = completeColumns(newcurrentColumns, columns);
              savePreferences([{ key: "columns", value: finalsColumns }]);
              pagination.onChangeColumns(finalsColumns);
            }}
            onChangeSort={(newSort) => {
              savePreferences([{ key: "sort", value: newSort }]);
              pagination.onChangeSort(newSort);
            }}
          />
        </Preferences>
      </Container>
      {openCreateEditManageInput && (
        <CreateEditManageInput refresh={tableRef?.current?.refreshTable} />
      )}
      {openDeleteManageInput && <DeleteManageInput refresh={() => {
        tableRef?.current?.refreshTable();
        pagination.onSearchChange("");
      }} />}
    </>
  );
};

export default InputsTab;
